import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { Box } from '@mui/system';
import { StyledPageTitle } from 'theme/styles';
import { FormInput } from 'components/molecules/FormInput';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Resolver, useForm } from 'react-hook-form';
import { LocationMap } from 'components/organisms/LocationMap';
import {
  ISearchParams,
  searchLocationAddressByCoords,
  searchLocationCoordsByAddress,
} from 'requests/legalEntities';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useLicenceContext } from 'models/licenceContext';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { ActionTypes } from 'state/actions/alert';
import { mapLEAddressToDataSource } from 'pages/Transactions/SelectLocationPage/helpers';
import { mapDictionaryToOptionProp, parseDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { isListNotEmpty } from 'utils/listUtils';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { ITableDataSource } from 'models/table';
import { FormSelect } from 'components/molecules/FormSelect';

const initialFormValues = {
  address: '',
  city: '',
  countryUniqueId: '',
};

const SelectLocationPage = () => {
  const [coords, setCoords] = useState<any>(null);
  const [address, setAddress] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [isAddressLoaded, setIsAddressLoaded] = useState(false);
  const [isLocateMapLoading, setIsLocateMapLoading] = useState<boolean>(false);
  const { state: routerState }: any = useLocation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { isOnlineLicence } = useLicenceContext();
  const { clearAlert, setAlert, samePageAlert, apiAlert, transactionAlert } = useAlert();
  const [selectedAddress, setSelectedAddress] = useState<ITableDataSource | null>(null);
  const [selectedAddressLoaded, setSelectedAddressLoaded] = useState(false);
  const { region, country, addressType, getRegion, getCountry, getAddressType } =
    useDictionary();
  const { selectedLanguage } = useLang();
  const {
    getLegalEntityStorageAddressesData,
    legalEntityStorageAddress,
    legalEntityStorageAddressLoaded,
  } = useLegalEntities();
  const [movedByLocateButton, setMovedByLocateButton] = useState(false);
  const canSelectOnlyExistingAddress = routerState?.canSelectOnlyExistingAddress === true;

  const schema = yup.object().shape({
    address: yup
      .string()
      .test(
        'addressRequired',
        t('general.field.validation.required'),
        (value) => (!!value && value.trim().length > 0) || canSelectOnlyExistingAddress,
      )
      .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255')),
    city: yup
      .string()
      .test(
        'cityRequired',
        t('general.field.validation.required'),
        (value) => (!!value && value.trim().length > 0) || canSelectOnlyExistingAddress,
      )
      .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255')),
    countryUniqueId: yup
      .string()
      .test(
        'countryUniqueIdRequired',
        t('general.field.validation.required'),
        (value) => (!!value && value.trim().length > 0) || canSelectOnlyExistingAddress,
      )
      .max(36, t('general.field.validation.maxCharacters').replace('{0}', '36')),
  });

  if (!routerState) {
    navigate(ERouteLinks.MiddleTransaction.replace(':id', id));
  }

  useEffect(() => {
    if (routerState && routerState.coords && coords === null && !routerState?.addressId) {
      setCoords(routerState.coords);
    }
  }, [routerState]);

  useEffect(() => {
    if (selectedAddress !== null) {
      setCoords([selectedAddress.latitude, selectedAddress.longitude]);
    }
  }, [selectedAddress]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_API_ALERT);
    },
    [],
  );

  useEffect(() => {
    if (routerState && routerState.searchAddressesFor) {
      getLegalEntityStorageAddressesData(routerState.searchAddressesFor);
    }
  }, [routerState]);

  useEffect(() => {
    if (legalEntityStorageAddressLoaded) {
      if (isListNotEmpty(legalEntityStorageAddress) && !!coords) {
        const legalEntityMatchingAddress = mapLEAddressToDataSource(
          legalEntityStorageAddress,
          parseDictionary(region, 'name'),
          parseDictionary(country, 'name'),
          parseDictionary(addressType, 'name'),
          id as string,
        ).find((it) => it.latitude === coords[0] && it.longitude === coords[1]);

        if (legalEntityMatchingAddress) {
          setSelectedAddress(legalEntityMatchingAddress);
        }
      }
      setSelectedAddressLoaded(true);
    }
  }, [legalEntityStorageAddress]);

  useEffect(() => {
    getRegion();
    getAddressType();
    getCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  let defaultLatitude: number = parseFloat(
    process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LATITUDE
      ? process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LATITUDE
      : '0',
  );
  let defaultLongitude: number = parseFloat(
    process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LONGITUDE
      ? process.env.REACT_APP_MAP_DEFAULT_COUNTRY_CENTER_LONGITUDE
      : '0',
  );
  let defaultZoom: number = parseInt(
    process.env.REACT_APP_MAP_DEFAULT_ZOOM ? process.env.REACT_APP_MAP_DEFAULT_ZOOM : '5',
    10,
  );

  if (routerState?.coords?.length && !routerState?.addressId) {
    [defaultLatitude, defaultLongitude] = routerState.coords;
    defaultZoom = 14;
  }

  const {
    control,
    reset,
    formState: { errors },
    getValues,
    setValue,
    setError,
    handleSubmit,
    clearErrors,
  } = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver,
  });

  const handleSearchLocationCoordsByAddress = async (
    searchParams: ISearchParams,
    successCallback: (p: any) => void,
    errorCallback: () => void,
    isOnline = true,
  ) => {
    if (getValues().address.length === 0) {
      setError('address', {
        type: 'custom',
        message: t('selectLocationOnMap.addressValidation.text'),
      });
      return;
    }
    if (getValues().address.length > 255) {
      setError('address', {
        type: 'custom',
        message: t('general.field.validation.maxCharacters').replace('{0}', '255'),
      });
      return;
    }
    searchLocationCoordsByAddress(
      searchParams,
      (d) => successCallback(d),
      errorCallback,
      isOnline,
    ).then((value) => {
      if (value) {
        clearErrors('address');
      } else {
        setError('address', {
          type: 'custom',
          message: t('selectLocationOnMap.addressValidation.text'),
        });
      }
    });
  };

  useEffect(() => {
    if (selectedAddress !== null) {
      setCoords([selectedAddress.latitude, selectedAddress.longitude]);
    }
  }, [selectedAddress]);

  useEffect(() => {
    if (coords && selectedAddressLoaded) {
      const lat = coords[0];
      const lon = coords[1];

      if (!selectedAddress) {
        searchLocationAddressByCoords(
          lat,
          lon,
          () =>
            setAlert(ActionTypes.SET_API_ALERT, {
              text: t('locationService.blockedDueTooManyRequests.error.message'),
              variant: EAlertVariants.error,
            }),
          isOnlineLicence(),
        ).then((value) => {
          if (value && value.address) {
            clearErrors('address');
            clearErrors('city');
            const place =
              value.address.city || value.address.town || value.address.village || '';
            const road = value.address.road || value.address.municipality || '';
            const houseNumber = value.address.house_number || '';
            const newAddress =
              `${place} ${road} ${houseNumber}`.trim() ||
              value.name ||
              value.display_name ||
              '';
            setAddress(newAddress);
            setCity(place);
            setIsAddressLoaded(true);
          } else {
            setError('address', {
              type: 'custom',
              message: t('selectLocationOnMap.enterAddress.text'),
            });
          }
        });
      } else {
        setAddress(selectedAddress.line1);
        setCity(selectedAddress.city);
        setIsAddressLoaded(true);
      }
    }
  }, [coords, selectedAddressLoaded]);

  useEffect(() => {
    if (country && country.length) {
      setValue('countryUniqueId', country[0].uniqueId as string);
    }
  }, [country]);

  // eslint-disable-next-line consistent-return
  const handleSetLocation = () => {
    if (
      (!canSelectOnlyExistingAddress && coords === null) ||
      (canSelectOnlyExistingAddress && selectedAddress === null)
    ) {
      setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
        text: canSelectOnlyExistingAddress
          ? t('selectLocationOnMap.selectAddress.text')
          : t('selectLocationOnMap.enterAddress.text'),
        variant: EAlertVariants.error,
      });

      return;
    }

    navigate(routerState.returnLink, {
      state: {
        ...routerState,
        coords,
        addressId: null,
        address: getValues('address').trim(),
        city: getValues('city').trim(),
        countryUniqueId: getValues('countryUniqueId'),
        isReloadCausedLocationSelect: true,
      },
    });
  };

  useEffect(() => {
    if (address && isAddressLoaded) {
      reset({ address, city, countryUniqueId: getValues('countryUniqueId') });
      setIsAddressLoaded(false);
    }
  }, [address, isAddressLoaded]);

  const handleSearchButtonClick = async (e: { preventDefault: () => void }) => {
    setIsLocateMapLoading(true);
    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    clearAlert(ActionTypes.CLEAR_API_ALERT);
    // eslint-disable-next-line no-restricted-globals
    e.preventDefault();
    await handleSearchLocationCoordsByAddress(
      {
        q: `${country ? country[0].name : ''} ${getValues().city} ${
          getValues().address
        }`.trim(),
      },
      (data) => {
        setMovedByLocateButton(true);
        setCoords([data[0].lat, data[0].lon]);
      },
      () =>
        setAlert(ActionTypes.SET_API_ALERT, {
          text: t('locationService.blockedDueTooManyRequests.error.message'),
          variant: EAlertVariants.error,
        }),
      isOnlineLicence(),
    );
    setTimeout(() => {
      setIsLocateMapLoading(false);
    }, 1000);
  };

  return (
    <div>
      <Breadcrumbs items={routerState?.breadcrumbs || []} />
      <Box sx={{ mb: 2 }}>
        <StyledPageTitle variant="h4">
          {t('selectLocationOnMap.selectLocationOnMap.header')}
        </StyledPageTitle>
      </Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        {canSelectOnlyExistingAddress
          ? t('selectLocationOnMap.strict.info.text')
          : t('selectLocationOnMap.info.text')}
      </Typography>
      {!isListNotEmpty(legalEntityStorageAddress) && canSelectOnlyExistingAddress && (
        <Box sx={{ mb: 2 }}>
          <Alert
            text={t('selectLocation.noAddressOnList.message')}
            variant={EAlertVariants.warning}
          />
        </Box>
      )}
      <form onSubmit={handleSubmit(handleSetLocation)}>
        {!canSelectOnlyExistingAddress && (
          <>
            <Grid sx={{ columnGap: 3 }}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormInput
                  name="address"
                  label={`${t('selectLocationOnMap.address.label')}*`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  placeholder={t('selectLocationOnMap.placeholder.text')}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 3 }} sx={{ paddingTop: { lg: '28px' } }}>
                <Button
                  label={t('selectLocationOnMap.search.button')}
                  variant={EButtonVariants.outlined}
                  size={EButtonSizes.small}
                  fullWidth
                  onClick={handleSearchButtonClick}
                  disabled={isLocateMapLoading}
                  isLoading={isLocateMapLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormInput
                  name="city"
                  label={`${t('selectLocationOnMap.city.label')}*`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  placeholder={t('selectLocationOnMap.cityPlaceholder.text')}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FormSelect
                  name="countryUniqueId"
                  options={mapDictionaryToOptionProp(country)}
                  setValue={setValue}
                  label={`${t('addAddress.country.label')}*`}
                  control={control}
                  errors={errors}
                  disabled
                />
              </Grid>
            </Grid>
            <Grid sx={{ columnGap: 5, rowGap: 1, my: 2 }}>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Typography variant="subtitle1" sx={{ display: 'flex' }}>
                  {t('selectLocationOnMap.latitude.label')}:{' '}
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    {coords !== null ? coords[0] : '-'}
                  </Typography>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' }}>
                <Typography variant="subtitle1" sx={{ display: 'flex' }}>
                  {t('selectLocationOnMap.longitude.label')}:{' '}
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    {coords !== null ? coords[1] : '-'}
                  </Typography>
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
        {samePageAlert && (
          <Box sx={{ marginBottom: '10px', mt: 2 }}>
            <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
          </Box>
        )}
        {apiAlert && (
          <Box sx={{ marginBottom: '10px', mt: 2 }}>
            <Alert text={apiAlert.text} variant={apiAlert.variant} />
          </Box>
        )}
        <LocationMap
          coords={coords !== null ? coords : [defaultLatitude, defaultLongitude]}
          dimensions={{ width: '100%', height: '600px' }}
          defaultZoom={defaultZoom}
          getCoordsCallback={(data: any) => {
            setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
              text: t('selectLocationOnMap.locationManuallyAdded.message'),
              variant: EAlertVariants.warning,
            });

            reset({ ...initialFormValues, countryUniqueId: getValues('countryUniqueId') });
            setCoords([data.lat, data.lng]);
          }}
          isOnline={isOnlineLicence()}
          pinDocked={!!selectedAddress || canSelectOnlyExistingAddress}
          pinHidden={!selectedAddress && canSelectOnlyExistingAddress}
          movedByLocateButton={movedByLocateButton}
          setMovedByLocateButton={setMovedByLocateButton}
        />
        <Grid container spacing={2} sx={{ mt: 5 }}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <Button
              fullWidth
              type="submit"
              label={t('selectLocationOnMap.setLocation.button')}
              variant={EButtonVariants.contained}
              sx={{ mr: 1 }}
              onClick={handleSubmit}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <Button
              fullWidth
              label={t('selectLocationOnMap.cancel.button')}
              variant={EButtonVariants.outlined}
              onClick={() => {
                navigate(routerState.returnLink, {
                  state: { ...routerState, isReloadCausedLocationSelect: true },
                });
              }}
            />
          </Grid>
          <Grid sx={{ mt: 4 }}>
            {transactionAlert && (
              <Box sx={{ mb: 2 }}>
                <Alert text={transactionAlert.text} variant={transactionAlert.variant} />
              </Box>
            )}
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export { SelectLocationPage };
